/**
 * SecureEmbed Tier 2 Dashboard API Service
 *
 * API client for communicating with backend services
 * Task 1.3: Added settingsApi for per-user settings
 */

import axios, { AxiosInstance, AxiosError } from 'axios';
import { STORAGE_KEYS, API_CONFIG } from '../constants/config';
import { performLogout, getIsLoggingOut } from '@secureembed/shared-crm';

// API Configuration - reads from runtime config.js
const getApiBaseUrl = (): string => {
  // First try runtime config (set by Terraform in config.js)
  const config = (window as any).SECUREEMBED_CONFIG;
  if (config?.API_ENDPOINT) {
    return config.API_ENDPOINT + '/api';
  }
  if (config?.API_URL) {
    return config.API_URL + '/api';
  }
  // C3: Fallback to env variable - no hardcoded API Gateway URL
  const url = import.meta.env.VITE_API_BASE_URL || '';
  if (!url) {
    console.error('API endpoint not configured');
  }
  return url;
};

const API_TIMEOUT = API_CONFIG.TIMEOUT_MS;

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for auth and dynamic base URL
apiClient.interceptors.request.use(
  (config) => {
    // Set baseURL dynamically on each request (config.js may load after module init)
    config.baseURL = getApiBaseUrl();

    const token = localStorage.getItem(STORAGE_KEYS.ID_TOKEN) || localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (!getIsLoggingOut() && error.response?.status === 401) {
      performLogout({
        storageKeys: [
          STORAGE_KEYS.AUTH_TOKEN,
          STORAGE_KEYS.ID_TOKEN,
          STORAGE_KEYS.REFRESH_TOKEN,
          STORAGE_KEYS.USER,
        ],
      });
    }
    return Promise.reject(error);
  }
);

// Types
export interface AnalyticsResponse {
  success: boolean;
  data: {
    tenant_id: string;
    time_range: string;
    period: {
      start: string;
      end: string;
    };
    summary?: {
      total_insights: number;
      by_type: Record<string, number>;
      unique_source_embeddings: number;
      daily_breakdown: Record<string, Record<string, number>>;
    };
    trends?: {
      granularity: string;
      data_points: number;
      trends: Array<{
        timestamp: string;
        total: number;
        by_type: Record<string, number>;
      }>;
    };
    performance?: {
      insights_processed: number;
      algorithms_used: Record<string, number>;
      processing_time?: {
        avg_ms: number;
        min_ms: number;
        max_ms: number;
      };
      confidence?: {
        avg: number;
        min: number;
        max: number;
      };
    };
  };
}

export interface InsightResponse {
  success: boolean;
  data: {
    insights: Array<{
      insight_id: string;
      insight_type: string;
      created_at: string;
      source_embedding_id?: string;
      provenance?: {
        algorithm?: string;
        confidence?: number;
      };
    }>;
    count: number;
    next_token?: string;
  };
}

export interface ExportResponse {
  success: boolean;
  data: {
    export_id?: string;
    download_url?: string;
    record_count: number;
    format: string;
    expires_in_seconds?: number;
    content?: string;
    export_timestamp?: string;
    message?: string;
  };
}

export interface ProvenanceResponse {
  success: boolean;
  data: {
    source_embedding_id: string;
    derived_insights_count: number;
    insights: Array<{
      insight_id: string;
      insight_type: string;
      created_at: string;
      provenance: Record<string, any>;
    }>;
  };
}

// Analytics API
export const analyticsApi = {
  /**
   * Get analytics data for dashboard
   */
  getAnalytics: async (
    metric: 'summary' | 'trends' | 'performance' | 'all' = 'all',
    timeRange: string = '7d',
    granularity: string = 'day'
  ): Promise<AnalyticsResponse> => {
    const response = await apiClient.get<AnalyticsResponse>('/analytics', {
      params: { metric, time_range: timeRange, granularity },
    });
    return response.data;
  },
};

// Insights API
export const insightsApi = {
  /**
   * List insights with optional filters
   */
  listInsights: async (params?: {
    insight_type?: string;
    limit?: number;
    next_token?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<InsightResponse> => {
    const response = await apiClient.get<InsightResponse>('/insights', { params });
    return response.data;
  },

  /**
   * Get a specific insight by ID
   */
  getInsight: async (insightId: string): Promise<any> => {
    const response = await apiClient.get(`/insights/${insightId}`);
    return response.data;
  },

  /**
   * Create a new insight
   */
  createInsight: async (data: {
    insight_type: string;
    insight_data: Record<string, any>;
    source_embedding_id?: string;
    source_embeddings?: string[];
    provenance_metadata?: Record<string, any>;
  }): Promise<any> => {
    const response = await apiClient.post('/insights', data);
    return response.data;
  },
};

// Provenance API
export const provenanceApi = {
  /**
   * Get provenance chain for an embedding
   */
  getProvenanceChain: async (
    embeddingId: string,
    depth: number = 10,
    includeRaw: boolean = false
  ): Promise<ProvenanceResponse> => {
    const response = await apiClient.get<ProvenanceResponse>(`/provenance/${embeddingId}`, {
      params: { depth, include_raw: includeRaw },
    });
    return response.data;
  },
};

// Export API
export const exportApi = {
  /**
   * Generate data export
   */
  createExport: async (params: {
    format: 'json' | 'csv';
    start_date?: string;
    end_date?: string;
    insight_type?: string;
    max_records?: number;
  }): Promise<ExportResponse> => {
    const response = await apiClient.post<ExportResponse>('/export', params);
    return response.data;
  },
};

// Search API (Tier 1)
export const searchApi = {
  /**
   * Search for similar embeddings
   */
  searchSimilar: async (params: {
    query: string;
    top_k?: number;
    filters?: Record<string, any>;
  }): Promise<any> => {
    const response = await apiClient.post('/search/similar', params);
    return response.data;
  },

  /**
   * Get metadata for embeddings
   */
  getMetadata: async (params: {
    embedding_ids?: string[];
    region?: string;
    date_from?: string;
    date_to?: string;
  }): Promise<any> => {
    const response = await apiClient.get('/metadata', { params });
    return response.data;
  },
};

// Aggregate API
export const aggregateApi = {
  /**
   * Aggregate insights across embeddings
   */
  aggregate: async (params: {
    source_embeddings: string[];
    aggregation_type: 'statistical' | 'count_by_type' | 'timeline' | 'merge';
    insight_type_filter?: string;
    aggregation_params?: Record<string, any>;
  }): Promise<any> => {
    const response = await apiClient.post('/aggregate', params);
    return response.data;
  },
};

// Backup API
export const backupApi = {
  listBackups: async (params?: Record<string, any>): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'list-backups', ...params });
    return response.data;
  },
  listVaults: async (): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'list-vaults' });
    return response.data;
  },
  createBackup: async (params: { resource_arn: string; vault_name?: string; move_to_cold_after_days?: number; delete_after_days?: number }): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'create-backup', ...params });
    return response.data;
  },
  restoreBackup: async (params: { recovery_point_arn: string; vault_name?: string; metadata?: Record<string, string> }): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'restore-backup', ...params });
    return response.data;
  },
  getBackupPlan: async (): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'get-backup-plan' });
    return response.data;
  },
  createBackupPlan: async (params: { plan_name: string; schedule?: string; retention_days?: number; cold_storage_days?: number; vault_name?: string; resource_arns?: string[]; account?: string; env?: string }): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'create-backup-plan', ...params });
    return response.data;
  },
  // Bug fix (Story #4487 — Bug 2): one-time on-demand path — starts jobs immediately,
  // no stray recurring plan, returns jobs_started for proper UI feedback.
  createBackupOndemand: async (params: { resource_arns: string[]; retention_days?: number; cold_storage_days?: number; vault_name?: string; account?: string; env?: string }): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'create-backup-ondemand', ...params });
    return response.data;
  },
  estimateCosts: async (params: { storage_gb: number; retention_days: number; cold_storage_days?: number; resource_types?: string[] }): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'estimate-costs', ...params });
    return response.data;
  },
  listResources: async (params?: Record<string, any>): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'list-resources', ...params });
    return response.data;
  },
  restoreSelective: async (params: { recovery_point_arns: string[]; vault_name?: string; restore_target?: string }): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'restore-selective', ...params });
    return response.data;
  },
  listSchedules: async (): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'list-schedules' });
    return response.data;
  },
  updateSchedule: async (params: { plan_id: string; plan_name?: string; schedule_expression?: string; retention_days?: number; vault_name?: string }): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'update-schedule', ...params });
    return response.data;
  },
  deleteSchedule: async (params: { plan_id: string }): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'delete-schedule', ...params });
    return response.data;
  },
  runSchedule: async (params: { plan_id: string }): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'run-schedule', ...params });
    return response.data;
  },
  listBackupContents: async (params?: { recovery_point_arn?: string; vault_name?: string; plan_id?: string }): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'list-backup-contents', ...params });
    return response.data;
  },
  listJobs: async (params?: { plan_id?: string; status?: string; resource_type?: string }): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'list-jobs', ...params });
    return response.data;
  },
  // Story #4482: Backup Catalogue + Vaults & DR tabs
  listCatalogue: async (params?: { account?: string; env?: string; tier?: string; kind?: string; search?: string; limit?: number }): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'list-catalogue', ...params });
    return response.data;
  },
  listVaultsStatus: async (params?: { include_dr?: boolean }): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'list-vaults-status', ...params });
    return response.data;
  },
  deleteRecoveryPoint: async (params: { vault_name: string; recovery_point_arn: string }): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'delete-recovery-point', ...params });
    return response.data;
  },
};

export default apiClient;

// Operations API (Portal-driven deploy/destroy/status)
export const operationsApi = {
  /**
   * Get overall status (all environments)
   */
  getStatus: async (): Promise<any> => {
    try {
      const response = await apiClient.get('/status');
      const data = response.data;
      // Check for HTML error response
      if (typeof data === 'string' && (data.includes('<!DOCTYPE') || data.includes('<html'))) {
        return { error: 'API not deployed', environments: {} };
      }
      return data;
    } catch (err: any) {
      console.warn('Status API error:', err?.message);
      return { error: err?.message || 'Failed to fetch status', environments: {} };
    }
  },

  /**
   * Trigger deployment from the portal
   */
  deploy: async (params: { env: string; layer: string; power_action?: boolean }): Promise<any> => {
    // Backend expects 'environment' not 'env'
    const response = await apiClient.post('/deploy', {
      environment: params.env,
      layer: params.layer,
      power_action: params.power_action,
    });
    return response.data;
  },

  /**
   * Trigger destroy from the portal
   */
  destroy: async (params: { env: string; layer: string; confirmation?: string; power_action?: boolean }): Promise<any> => {
    // Backend expects 'environment' not 'env'
    const response = await apiClient.post('/destroy', {
      environment: params.env,
      layer: params.layer,
      confirmation: params.confirmation,
      power_action: params.power_action,
    });
    return response.data;
  },

  /**
   * Stop an active deployment/destroy operation
   */
  stop: async (params: { env: string; build_id?: string }): Promise<any> => {
    // Backend expects 'environment' not 'env'
    const response = await apiClient.post('/deploy/stop', { environment: params.env, build_id: params.build_id });
    return response.data;
  },
};

// Power API (start/stop environment resources)
export const powerApi = {
  getState: async (params: { env: string }): Promise<any> => {
    const response = await apiClient.get('/power', { params: { environment: params.env } });
    return response.data;
  },
  setPower: async (params: { env: string; action: 'start' | 'stop'; eks_desired?: number; confirm?: boolean }): Promise<any> => {
    const response = await apiClient.post('/power', {
      environment: params.env,
      action: params.action,
      eks_desired: params.eks_desired,
      confirm: params.confirm,
    });
    return response.data;
  },
};

// Demo Environment API (Environment Control Plane — US #4440)
// Portal-driven demo/test environment + demo-data ops. Routes resolve under
// /api/demo-env/{action}; the backend dispatches on the trailing segment.
// Rich per-baseline metadata (manifest v2, Story #4729). `baselines` (names
// only) is retained for back-compat; `baseline_details` is what the Baselines
// card renders — server-sorted pinned-first then newest-first.
export interface BaselineDetail {
  name: string;
  captured_at?: string | null;
  captured_by?: string | null;
  description?: string;
  origin?: 'portal-save' | 'cli-export' | 'auto-pre-promote' | 'scenario' | string;
  protected?: boolean;
  env?: string;
  account?: string;
  app_version?: { git_sha?: string; recorded_at?: number } | null;
  tables?: number;
  items?: number;
  size_bytes?: number;
  objects?: number;
}

export interface DemoEnvStatus {
  env: string;
  account?: string;
  opensearch?: {
    running: boolean;
    processing?: boolean;
    endpoint?: string | null;
    // Story #4940 single-shared-domain model: set when this env runs no domain
    // of its own and consumes another env's shared domain over VPC peering.
    shared?: { domain: string; endpoint?: string; health?: string | null; healthy: boolean };
  };
  nat?: { count: number; running: boolean };
  data?: Record<string, number | null>;
  snapshots?: string[];
  baselines?: string[];
  baseline_details?: BaselineDetail[];
  version?: string;
  error?: string;
}

// Result payload of a COMPLETED reinstate job (jobStatus().result). The backend
// verifies against live AWS after the Terraform apply — the UI renders these
// observed flags, never a bare "done" nobody checked.
export interface ReinstateResult {
  verified?: boolean;
  opensearch_running?: boolean;
  nat_running?: boolean;
  baseline?: string | null;
}

// Release-Promotion Control Plane (extends the Environment Control Plane).
// One promotion job moves work UP the release ladder (dev→test→uat→prod) from
// any lower source to any higher target, per-portal, in four modes:
//   1 Design only · 2 Design+Backend · 3 Design+Backend+Data · 4 Data only.
// Mode 4 is the absorbed "Replicate from DEV" (generalised to any source).
export type PromoteMode = 1 | 2 | 3 | 4;

export interface PromoteOptions {
  source: string;
  target: string;
  portal: string;
  mode: PromoteMode;
  autoSpinUp?: boolean;
  saveBaselineFirst?: boolean;
  // For a recovery [Re-run from data stage] — resume at promote-data, skipping
  // the already-captured baseline step.
  resumeStage?: string;
  // Type-to-confirm value (target name; 'deploy-prod' for prod).
  confirmation?: string;
  // W3: per-promotion gate waivers, keyed by gate stage name
  // ('gate-version' | 'gate-sechub' | 'gate-drift'); value is the justification,
  // recorded on the stage + audit trail.
  gateOverrides?: Record<string, string>;
  // W3: pin a frozen Golden Config sha (a Demo Profile run sets this).
  pinGitSha?: string;
}

// One stage of a multi-stage promote job, aggregated into a live checklist.
export interface PromoteStage {
  name: string;
  status: 'pending' | 'running' | 'complete' | 'failed' | 'blocked' | 'skipped' | 'cancelled';
  build_id?: string;
  message?: string;
  // W3: gate stages carry a decision detail (pass/blocked, reason, note, override,
  // as_of, critical_high, drifted, …) for the UI to render inline.
  detail?: Record<string, any>;
}

export interface PromotionJob {
  id?: string;
  job?: string;
  op?: string;
  mode?: PromoteMode;
  source?: string;
  target?: string;
  portal?: string;
  status?: 'running' | 'complete' | 'failed' | 'blocked' | 'cancelled';
  stage?: string;
  stages?: PromoteStage[];
  build_id?: string;
  started?: number;
  git_sha?: string;
  manifest_missing?: boolean;
  auto_baseline?: string;
  // Non-blocking readiness warnings attached at preflight (e.g. the target's
  // staged CodeBuild source zip is stale/missing) — rendered amber, never fatal.
  warnings?: string[];
  error?: string;
  message?: string;
  // W3: when a gate blocks, `blocked` carries {gate, reason, …}.
  blocked?: Record<string, any> & { gate?: string; reason?: string };
  cancel_requested?: boolean;
  result?: Record<string, any> & { auto_baseline?: string };
}

// R2: cached drift verdict — the scheduled compute-drift compares the target's
// live fingerprint (api-handler CodeSha256 + frontend bundle ETag) against the
// deployed-manifest recorded at the last promotion. drifted null = no baseline
// yet (honest unknown, never guessed).
export interface DriftVerdict {
  drifted: boolean | null;
  changes?: number;
  details?: Array<{ component: string; recorded: string; live: string | null }>;
  baseline_git_sha?: string | null;
  baseline_recorded_at?: number;
  as_of?: string;
  basis?: string;
  note?: string;
}

export interface DriftResponse {
  env: string;
  portal: string;
  verdict: DriftVerdict | null;
  error?: string;
}

// Promotion Packages — a promotable "unit of work" (a story tracked by the P0
// registry) that is on the source env but not yet on the target.
export interface PromotionUnit {
  story: string;
  title?: string;
  sha: string;
  on_envs: string[];
  at?: string;
  order?: number;
  promo_status?: string;
}
export interface PackageResolution {
  pin_sha: string | null;
  pin_story?: string;
  selected: string[];
  dependencies: string[];
  included: Array<{ story: string; title?: string; sha: string; order: number }>;
  error?: string | null;
}
export interface PromotionUnitsResponse {
  source: string;
  target: string;
  portal: string;
  promotable: PromotionUnit[];
  already_on_target: PromotionUnit[];
  total: number;
  preview?: PackageResolution;
  error?: string;
}
export interface DependencyDetail {
  session_id: string;
  label: string;
  stories: string[];
  shared_files: string[];  // the exact source files that couple the two sessions
  shared_count: number;    // total shared (shared_files is capped for display)
}
export interface SessionBatch {
  session_id: string;
  label: string;
  lead: string;
  at: string;
  order: number;
  stories: string[];
  commit_count: number;
  file_count: number;
  files_truncated?: boolean;
  sha: string;
  depends_on: string[];   // earlier sessions this one shares source files with
  depends_on_detail?: DependencyDetail[];  // WHY each dependency is coupled (shared files)
  on_target?: boolean;    // already deployed up to the selected target env
}
export interface SessionsResponse {
  portal: string;
  source: string;
  target: string;
  sessions: SessionBatch[];
  baseline?: { at: string; sha: string };  // last-known deploy to the target (on-target cutoff)
  error?: string;
}
export interface AdoWorkItem {
  id: number;
  title: string | null;   // null when the item couldn't be read (missing / no access)
  type: string;
  state: string;
  tasks: AdoWorkItem[];    // child work items (usually Tasks)
}
export interface AdoWorkItemsResponse {
  items: Record<string, AdoWorkItem>;  // keyed by story id (string)
  error?: string;
}
export interface PackageHistoryRow {
  target: string;
  pin_sha: string;
  selected: string[];
  dependencies: string[];
  included: string[];
  job?: string;
  actor?: string;
  at: string;
}

// W3: a Golden Config is a frozen, hash-pinned build captured from a source env.
export interface GoldenConfig {
  name: string;
  portal?: string;
  git_sha?: string | null;
  source_env?: string;
  pinned?: boolean;
  description?: string;
  created_by?: string;
  created?: number | string;
  updated?: number | string;
}

// W3: a Demo Profile binds a Golden Config + Data Set + target for one-click run.
export interface DemoProfile {
  name: string;
  portal?: string;
  golden_config?: string;
  data_set?: string;
  target?: string;
  mode?: number | string;
  created_by?: string;
  updated?: number | string;
}

// Per-(target[,portal]) readiness as returned by op_targets — the single
// render-time source the UI reads for all show/hide, disabled and badge state.
export interface TargetReadiness {
  ready: 'ready' | 'blocked';
  reason?: string;
  deployable: boolean;
  first_time?: boolean;
  portals?: Record<string, TargetReadiness>;
}

export interface TargetsMatrix {
  source?: string;
  // Per-target readiness; each target may carry a per-portal sub-matrix when
  // `portal` was omitted from the request.
  targets?: Record<string, TargetReadiness>;
  error?: string;
}

// ---------------------------------------------------------------------------
// Environment lifecycle jobs + demo-hold (W2 — Environments hub / detail).
// Every lifecycle/data operation (reset, snapshot, save, restore, teardown,
// stand-up, promote) runs as a *tracked* job. `lock` names the single active
// mutating job for the env (so two operators cannot mutate at once); `hold` is
// an admin demo-hold that blocks destructive/data ops during a live demo.
// ---------------------------------------------------------------------------
export interface EnvJobStep {
  name: string;
  status: string;
}

export interface EnvJob {
  job_id: string;
  op: string;
  name?: string;
  actor?: string;
  status: 'running' | 'complete' | 'failed' | 'pending' | 'blocked' | string;
  steps?: EnvJobStep[];
  started?: number;
  heartbeat?: number;
  finished?: number;
}

export interface EnvLock {
  owner_job?: string;
}

export interface EnvHold {
  held_by?: string;
  reason?: string;
  expiry?: number;
}

export interface EnvJobsResponse {
  env: string;
  jobs: EnvJob[];
  lock: EnvLock | null;
  hold: EnvHold | null;
  error?: string;
}

export interface EnvHoldResponse {
  env: string;
  hold: EnvHold | null;
  error?: string;
}

export const demoEnvApi = {
  getStatus: async (params: { env?: string } = {}): Promise<DemoEnvStatus> => {
    const env = params.env || 'test';
    try {
      const response = await apiClient.get('/demo-env/status', { params: { env } });
      return safeApiResponse<DemoEnvStatus>(response.data, { env, error: 'API not deployed' });
    } catch (err: any) {
      // A per-env HTTP failure must degrade to an honest `error` status: the
      // fleet view fetches every env in one Promise.all, so a thrown 500 for
      // one spoke would blank ALL cards — and an undefined status renders as
      // "torn down", which is a lie for an always-on env.
      const message =
        err?.response?.data?.error || err?.message || 'status unavailable';
      return { env, error: String(message) };
    }
  },
  resetData: async (params: { env?: string; name?: string; portal?: string }): Promise<any> => {
    const response = await apiClient.post('/demo-env/reset-data', {
      env: params.env || 'test',
      name: params.name,
      portal: params.portal,
    });
    return response.data;
  },
  // T1 cost teardown — destroys the OpenSearch domain + NAT gateway (Bug #4851).
  // Asynchronous: returns { job, build_id } immediately and the caller polls
  // getJobStatus. The job reaches 'complete' only after AWS confirms both
  // resources are actually gone; a green build alone is not enough.
  //
  // This replaced powerApi.setPower({action:'stop'}), which the button used to
  // call. That handler manages EKS node groups and Aurora clusters, neither of
  // which this platform runs, so it reported success while destroying nothing
  // and TEST billed at full rate for a month.
  teardown: async (params: { env?: string; tier?: 'cost' | 'data' }): Promise<any> => {
    const response = await apiClient.post('/demo-env/teardown', {
      env: params.env || 'test',
      tier: params.tier || 'cost',
    });
    return response.data;
  },
  // Reinstate a torn-down environment — the way back from a T1/T2 teardown.
  // Terraform rebuilds the OpenSearch domain + NAT gateway in the spoke, then,
  // when `baseline` is named, restores that data set (records + search index
  // together). Omit `baseline` for infrastructure only (existing data is left
  // as-is; safe to run even when everything is already up — it re-verifies).
  // Asynchronous: returns { job } and the caller polls jobStatus — with a LONG
  // cap (the OpenSearch rebuild alone runs ~20–40 min), not the 5-min default.
  reinstate: async (params: { env?: string; baseline?: string }): Promise<{ job?: string; error?: string }> => {
    const body: Record<string, any> = { env: params.env || 'test' };
    if (params.baseline) body.baseline = params.baseline;
    const response = await apiClient.post('/demo-env/reinstate', body);
    return response.data;
  },
  // Apply a scenario file (fence-checked fixture replace) — narrower than
  // resetData: scoped to the scenario's own rows, legal on dev as well as
  // test (SCENARIO_ENV_ALLOWLIST, T5b). force overrides a demo-hold held by
  // a DIFFERENT caller (holder-only relaxation, D7); the same holder never
  // needs it. Synchronous — the response carries the write/delete counts.
  scenarioReset: async (params: { env?: string; scenario?: string; force?: boolean }): Promise<any> => {
    const response = await apiClient.post('/demo-env/scenario-reset', {
      env: params.env || 'test',
      scenario: params.scenario,
      force: params.force,
    });
    return response.data;
  },
  snapshot: async (params: { env?: string; name?: string }): Promise<any> => {
    const response = await apiClient.post('/demo-env/snapshot', {
      env: params.env || 'test',
      name: params.name,
    });
    return response.data;
  },
  restore: async (params: { env?: string; name?: string }): Promise<any> => {
    const response = await apiClient.post('/demo-env/restore', {
      env: params.env || 'test',
      name: params.name,
    });
    return response.data;
  },
  saveDataset: async (params: { env?: string; name?: string; description?: string }): Promise<any> => {
    const response = await apiClient.post('/demo-env/save-dataset', {
      env: params.env || 'test',
      name: params.name,
      description: params.description,
    });
    return response.data;
  },

  // Metadata-only edit: description and/or the pinned (delete-protected) flag.
  updateBaseline: async (params: {
    env?: string;
    name: string;
    description?: string;
    protected?: boolean;
  }): Promise<any> => {
    const body: Record<string, any> = { env: params.env || 'test', name: params.name };
    if (params.description !== undefined) body.description = params.description;
    if (params.protected !== undefined) body.protected = params.protected;
    const response = await apiClient.post('/demo-env/update-baseline', body);
    return response.data;
  },
  // Atomically restore BOTH layers of a baseline (DynamoDB records + OpenSearch
  // index) under one name, so the two stores can never drift.
  restoreBaseline: async (params: { env?: string; name?: string }): Promise<any> => {
    const response = await apiClient.post('/demo-env/restore-baseline', {
      env: params.env || 'test',
      name: params.name,
    });
    return response.data;
  },
  // Poll the state of an async Save/Restore/Rename job (running | complete | failed)
  // so the UI can show a live progress indicator instead of manual Refresh.
  jobStatus: async (params: { env?: string; id: string }): Promise<any> => {
    const response = await apiClient.get('/demo-env/job-status', {
      params: { env: params.env || 'test', id: params.id },
    });
    return response.data;
  },
  // Delete BOTH halves of a baseline (records export + OpenSearch snapshot). Synchronous.
  deleteBaseline: async (params: { env?: string; name: string }): Promise<any> => {
    const response = await apiClient.post('/demo-env/delete-baseline', {
      env: params.env || 'test',
      name: params.name,
    });
    return response.data;
  },
  // Rename a baseline (clone snapshot + copy records + delete old). Async (returns a job).
  renameBaseline: async (params: { env?: string; name: string; new: string }): Promise<any> => {
    const response = await apiClient.post('/demo-env/rename-baseline', {
      env: params.env || 'test',
      name: params.name,
      new: params.new,
    });
    return response.data;
  },
  // Start a promotion job (Release-Promotion Control Plane). Async — returns 202 {job}.
  // Moves work UP the ladder from `source` to `target`, per portal, in one of four modes.
  // The backend reads snake_case options, so translate here (a prior camelCase body
  // silently fell back to backend defaults — gate overrides MUST reach the backend).
  promote: async (params: PromoteOptions): Promise<PromotionJob> => {
    const body: Record<string, any> = {
      source: params.source, target: params.target,
      portal: params.portal, mode: params.mode,
    };
    if (params.autoSpinUp !== undefined) body.auto_spin_up = params.autoSpinUp;
    if (params.saveBaselineFirst !== undefined) body.save_baseline_first = params.saveBaselineFirst;
    if (params.resumeStage) body.resume_stage = params.resumeStage;
    if (params.confirmation) body.confirmation = params.confirmation;
    if (params.gateOverrides) body.gate_overrides = params.gateOverrides;
    if (params.pinGitSha) body.pin_git_sha = params.pinGitSha;
    const response = await apiClient.post('/demo-env/promote', body);
    return response.data;
  },
  // W3: cancel a running promotion — honoured at the next stage boundary (an
  // in-flight build or data slice finishes first). Admin.
  promotionCancel: async (params: { target: string; id: string }): Promise<PromotionJob> => {
    const response = await apiClient.post('/demo-env/promotion-cancel', params);
    return response.data;
  },
  // W3/R3: roll a promotion back. Default = re-apply the pre-promotion data
  // baseline. includeBuild=true ALSO re-applies the prior deployed-manifest sha
  // as a pinned promotion (build + data revert in one ordered job; build-only
  // when the promotion captured no data baseline). Admin; same guard-rails as
  // any destructive op (never prod/uat, demo-hold blocks).
  promotionRollback: async (params: { target: string; id: string; portal?: string; includeBuild?: boolean }): Promise<PromotionJob> => {
    const { includeBuild, ...rest } = params;
    const response = await apiClient.post('/demo-env/promotion-rollback',
      includeBuild ? { ...rest, include_build: true } : rest);
    return response.data;
  },
  // R2: cached drift verdict for the env-card badge / Overview banner.
  getDrift: async (env: string, portal = 'innovateflow'): Promise<DriftResponse> => {
    const response = await apiClient.get('/demo-env/drift', { params: { env, portal } });
    return safeApiResponse<DriftResponse>(response.data, { env, portal, verdict: null });
  },
  // R2: admin "Recompute drift" — synchronous fingerprint + verdict refresh.
  computeDrift: async (env: string, portal = 'innovateflow'): Promise<DriftResponse> => {
    const response = await apiClient.post('/demo-env/compute-drift', { env, portal });
    return response.data;
  },
  // Poll a multi-stage promotion job (running | complete | failed | blocked).
  // Reuses the extended job-vault schema (stages[], auto_baseline, build_id, …).
  promotionStatus: async (params: { target: string; id: string }): Promise<PromotionJob> => {
    const response = await apiClient.get('/demo-env/promotion-status', { params });
    return response.data;
  },
  // Promotion Packages: the work-items on `source` not yet on `target`, plus a
  // dependency-sweep preview. Read-only; any authenticated caller.
  listPromotionUnits: async (
    source = 'dev', target = 'test', portal = 'innovateflow',
  ): Promise<PromotionUnitsResponse> => {
    const response = await apiClient.get('/demo-env/promotion-units', { params: { source, target, portal } });
    return safeApiResponse<PromotionUnitsResponse>(response.data, {
      source, target, portal, promotable: [], already_on_target: [], total: 0,
    });
  },
  // Promotion Packages by dev SESSION (the logical model): each batch is a session, with
  // a real file-overlap dependency graph between sessions.
  listSessionBatches: async (portal = 'innovateflow', target = 'test', source = 'dev'): Promise<SessionsResponse> => {
    const response = await apiClient.get('/demo-env/promotion-sessions', { params: { portal, target, source } });
    return safeApiResponse<SessionsResponse>(response.data, { portal, source, target, sessions: [] });
  },
  // ADO story titles + their child tasks, for the Promotion Packages tree. Read-only.
  adoWorkItems: async (ids: string[]): Promise<AdoWorkItemsResponse> => {
    if (!ids.length) return { items: {} };
    const response = await apiClient.get('/demo-env/ado-workitems', { params: { ids: ids.join(',') } });
    return safeApiResponse<AdoWorkItemsResponse>(response.data, { items: {} });
  },
  // Promote a SELECTED set of story ids as one package. The backend resolves the
  // selection to a single pin sha (sweeping in earlier un-promoted units as
  // dependencies) and drives the same staged promotion engine as promote(). Admin.
  promotePackage: async (params: {
    source?: string; target: string; portal?: string; storyIds?: string[]; sessionIds?: string[];
    mode?: PromoteMode; confirmation?: string; gateOverrides?: Record<string, string>;
  }): Promise<PromotionJob & { package?: PackageResolution }> => {
    const body: Record<string, any> = {
      source: params.source ?? 'dev', target: params.target,
      portal: params.portal ?? 'innovateflow', mode: params.mode ?? 4,
    };
    if (params.storyIds) body.story_ids = params.storyIds;
    if (params.sessionIds) body.session_ids = params.sessionIds;
    if (params.confirmation) body.confirmation = params.confirmation;
    if (params.gateOverrides) body.gate_overrides = params.gateOverrides;
    const response = await apiClient.post('/demo-env/promote-package', body);
    return response.data;
  },
  // Append-only history of package promotions (for the history panel + rollback picker).
  listPackageHistory: async (
    portal = 'innovateflow', target?: string,
  ): Promise<{ portal: string; target: string; history: PackageHistoryRow[] }> => {
    const response = await apiClient.get('/demo-env/promotion-package-history', { params: { portal, ...(target ? { target } : {}) } });
    return safeApiResponse(response.data, { portal, target: target ?? 'all', history: [] });
  },
  // Render-time readiness/deployability matrix for the UI. ONE call (portal omitted)
  // returns the full per-target/per-portal matrix so the UI never probes N×M itself.
  targets: async (params: { source: string; portal?: string }): Promise<TargetsMatrix> => {
    const response = await apiClient.get('/demo-env/targets', { params });
    return response.data;
  },
  // Environment activity: the tracked lifecycle/data jobs for an env, plus the
  // current lock (single active mutating job) and demo-hold, in one call.
  getEnvJobs: async (env: string): Promise<EnvJobsResponse> => {
    const response = await apiClient.get('/demo-env/env-jobs', { params: { env } });
    return safeApiResponse<EnvJobsResponse>(response.data, {
      env,
      jobs: [],
      lock: null,
      hold: null,
      error: 'API not deployed',
    });
  },
  // Current demo-hold for an env (blocks resets/destructive ops during a live demo).
  getHold: async (env: string): Promise<EnvHoldResponse> => {
    const response = await apiClient.get('/demo-env/env-hold', { params: { env } });
    return safeApiResponse<EnvHoldResponse>(response.data, { env, hold: null });
  },
  // Set a demo-hold (admin): a reason + TTL in seconds.
  setHold: async (env: string, reason: string, ttlSec: number): Promise<EnvHoldResponse> => {
    const response = await apiClient.post('/demo-env/env-hold', { env, reason, ttl_sec: ttlSec });
    return response.data;
  },
  // Clear the demo-hold (admin). DELETE carries the env in the body.
  clearHold: async (env: string): Promise<EnvHoldResponse> => {
    const response = await apiClient.delete('/demo-env/env-hold', { data: { env } });
    return response.data;
  },
  // Back-compat alias for the absorbed "Replicate from DEV" card — maps to mode 4.
  pullFromDev: async (): Promise<PromotionJob> => {
    return demoEnvApi.promote({ source: 'dev', target: 'test', portal: 'innovateflow', mode: 4 });
  },

  // W3 Releases registry — Golden Configs (frozen, hash-pinned builds).
  listGoldenConfigs: async (portal = 'innovateflow'): Promise<{ portal: string; golden_configs: GoldenConfig[] }> => {
    const response = await apiClient.get('/demo-env/golden-configs', { params: { portal } });
    return safeApiResponse(response.data, { portal, golden_configs: [] });
  },
  captureGoldenConfig: async (params: { name: string; portal?: string; source?: string; description?: string; git_sha?: string }): Promise<{ golden_config: GoldenConfig }> => {
    const response = await apiClient.post('/demo-env/golden-configs', {
      portal: 'innovateflow', source: 'dev', ...params,
    });
    return response.data;
  },
  deleteGoldenConfig: async (name: string, portal = 'innovateflow'): Promise<any> => {
    const response = await apiClient.delete('/demo-env/golden-configs', { params: { name, portal } });
    return response.data;
  },

  // W3 Releases registry — Demo Profiles (golden config + data set + target).
  listDemoProfiles: async (portal = 'innovateflow'): Promise<{ portal: string; demo_profiles: DemoProfile[] }> => {
    const response = await apiClient.get('/demo-env/demo-profiles', { params: { portal } });
    return safeApiResponse(response.data, { portal, demo_profiles: [] });
  },
  saveDemoProfile: async (params: { name: string; golden_config: string; target?: string; data_set?: string; mode?: number; portal?: string }): Promise<{ demo_profile: DemoProfile }> => {
    const response = await apiClient.post('/demo-env/demo-profiles', {
      portal: 'innovateflow', target: 'test', mode: 3, ...params,
    });
    return response.data;
  },
  deleteDemoProfile: async (name: string, portal = 'innovateflow'): Promise<any> => {
    const response = await apiClient.delete('/demo-env/demo-profiles', { params: { name, portal } });
    return response.data;
  },
  // One-click: stand the target up on the profile's pinned build + seed its data.
  runDemoProfile: async (name: string, portal = 'innovateflow'): Promise<PromotionJob> => {
    const response = await apiClient.post('/demo-env/demo-profiles/run', { name, portal });
    return response.data;
  },
};

// Resize API (scale OpenSearch/Aurora/EKS)
export const resizeApi = {
  getConfig: async (params: { env: string }): Promise<any> => {
    const response = await apiClient.get('/resize', { params: { environment: params.env } });
    return response.data;
  },
  resize: async (params: { env: string; service: 'opensearch' | 'aurora' | 'eks'; config: Record<string, any> }): Promise<any> => {
    const response = await apiClient.post('/resize', {
      environment: params.env,
      service: params.service,
      config: params.config,
    });
    return response.data;
  },
};

// Ingestion API (Portal control — multi-datastore scheduling)
export const ingestionApi = {
  getStatus: async (params: { env: string; include_failed?: boolean }): Promise<any> => {
    const response = await apiClient.get('/ingestion', { params });
    return response.data;
  },
  run: async (params: { env: string; datastore?: string; options?: Record<string, any> }): Promise<any> => {
    const response = await apiClient.post('/ingestion', { ...params, action: 'run' });
    return response.data;
  },
  schedule: async (params: { env: string; schedule: string }): Promise<any> => {
    const response = await apiClient.post('/ingestion', { ...params, action: 'schedule' });
    return response.data;
  },
  stop: async (params: { env: string }): Promise<any> => {
    const response = await apiClient.post('/ingestion', { ...params, action: 'stop' });
    return response.data;
  },
  getDetails: async (params: { env: string; executionName: string }): Promise<any> => {
    const response = await apiClient.post('/ingestion', { ...params, action: 'details' });
    return response.data;
  },
  deleteData: async (params: { env: string; executionName?: string; executionNames?: string[] }): Promise<any> => {
    const response = await apiClient.post('/ingestion', { ...params, action: 'delete' });
    return response.data;
  },
  getLogs: async (params: {
    env: string;
    executionName?: string;
    executionArn?: string;
    nextToken?: string;
    limit?: number;
  }): Promise<any> => {
    const response = await apiClient.post('/ingestion', { ...params, action: 'logs' });
    return response.data;
  },
  updateSchedule: async (params: {
    env: string;
    enabled: boolean;
    scheduleExpression: string;
    region?: string;
    dataTypes?: string[];
    updatedOnly?: boolean;
    startDate?: string;
    endDate?: string;
    endDateCurrent?: boolean;
  }): Promise<any> => {
    const response = await apiClient.post('/ingestion', {
      ...params,
      action: 'updateSchedule'
    });
    return response.data;
  },
  // Old per-datastore schedule methods removed — use jobsApi.scheduleJob instead
  // Activity monitoring endpoints
  getActivity: async (params: { env: string }): Promise<any> => {
    const response = await apiClient.post('/ingestion', {
      action: 'activity',
      environment: params.env,
    });
    return response.data;
  },
  getActivityHistory: async (params: { env: string }): Promise<any> => {
    const response = await apiClient.post('/ingestion', {
      action: 'activity-history',
      environment: params.env,
    });
    return response.data;
  },
  getActivityResources: async (params: { env: string }): Promise<any> => {
    const response = await apiClient.post('/ingestion', {
      action: 'activity-resources',
      environment: params.env,
    });
    return response.data;
  },
  getJobsList: async (params: { env: string; limit?: number }): Promise<any> => {
    const response = await apiClient.post('/ingestion', {
      action: 'jobs-list',
      environment: params.env,
      limit: params.limit || 20,
    });
    return response.data;
  },
  getJobsProgress: async (params: { env: string; job_id: string }): Promise<any> => {
    const response = await apiClient.post('/ingestion', {
      action: 'jobs-progress',
      environment: params.env,
      job_id: params.job_id,
    });
    return response.data;
  },
  startJobProfile: async (params: { env: string; profile: string }): Promise<any> => {
    const response = await apiClient.post('/ingestion', {
      action: 'jobs-start',
      environment: params.env,
      profile: params.profile,
    });
    return response.data;
  },
  getSourcesForDatabase: async (database: string): Promise<{ database: string; sources: any[]; total: number }> => {
    const response = await apiClient.post('/ingestion', {
      action: 'sources-for-database',
      database,
    });
    return response.data;
  },
  getCollaboratorStats: async (params: { env: string }): Promise<any> => {
    const response = await apiClient.post('/ingestion', {
      action: 'collaborator-stats',
      environment: params.env,
    });
    return response.data;
  },
  getResourceBreakdown: async (params: { env: string }): Promise<any> => {
    const response = await apiClient.post('/ingestion', {
      action: 'activity-resources-breakdown',
      environment: params.env,
    });
    return response.data;
  },
};

// Ingestion Jobs API (dedicated Jobs Lambda — CRUD + lifecycle)
export interface IngestionJobConfig {
  target_database: string;
  target_type?: string;
  total_target_count?: number;
  sources?: Record<string, any>[];
  mode?: 'incremental' | 'bulk';
  focus_areas?: string[];
  cleanup_raw_data?: boolean;
  embedding_config?: { model_id?: string; dimensions?: number };
  metadata_fields?: string[];
  job_name?: string;
  schedule_expression?: string;
  schedule_enabled?: boolean;
  topic_proportions?: Record<string, number>;
  source_proportions?: Record<string, number>;
  status?: string;
}

export interface IngestionJob {
  job_id: string;
  target_database: string;
  target_type: string;
  status: 'PENDING' | 'RUNNING' | 'PAUSED' | 'COMPLETED' | 'FAILED' | 'CANCELLED' | 'SCHEDULED' | 'ON_HOLD' | 'DRAFT';
  mode: 'incremental' | 'bulk';
  total_target_count: number;
  progress_pct: number;
  sources: Record<string, any>[];
  created_at: string;
  updated_at: string;
  created_by?: string;
  job_name?: string;
  schedule_expression?: string;
  schedule_enabled?: boolean;
  topic_proportions?: Record<string, number>;
  source_proportions?: Record<string, number>;
  run_history?: RunRecord[];
}

export interface RunRecord {
  run_id: string;
  start_time: string;
  end_time: string;
  status: string;
  records_processed: number;
  records_failed: number;
  errors: string[];
  per_source_stats: Record<string, any>;
}

export const jobsApi = {
  createJob: async (config: IngestionJobConfig): Promise<IngestionJob> => {
    const response = await apiClient.post('/ingestion/jobs', config);
    return response.data;
  },
  getJob: async (jobId: string): Promise<IngestionJob> => {
    const response = await apiClient.get(`/ingestion/jobs/${jobId}`);
    return response.data;
  },
  listJobs: async (params?: { status?: string; database?: string }): Promise<{ jobs: IngestionJob[] }> => {
    const response = await apiClient.get('/ingestion/jobs', { params });
    return response.data;
  },
  updateJob: async (jobId: string, updates: Record<string, any>): Promise<IngestionJob> => {
    const response = await apiClient.put(`/ingestion/jobs/${jobId}`, updates);
    return response.data;
  },
  startJob: async (jobId: string): Promise<IngestionJob> => {
    const response = await apiClient.post(`/ingestion/jobs/${jobId}/start`);
    return response.data;
  },
  pauseJob: async (jobId: string): Promise<IngestionJob> => {
    const response = await apiClient.put(`/ingestion/jobs/${jobId}`, { action: 'pause' });
    return response.data;
  },
  cancelJob: async (jobId: string): Promise<IngestionJob> => {
    const response = await apiClient.put(`/ingestion/jobs/${jobId}`, { action: 'cancel' });
    return response.data;
  },
  resumeJob: async (jobId: string): Promise<IngestionJob> => {
    const response = await apiClient.put(`/ingestion/jobs/${jobId}`, { action: 'resume' });
    return response.data;
  },
  getJobProfiles: async (): Promise<any> => {
    const response = await apiClient.get('/ingestion/jobs', { params: { profiles: true } });
    return response.data;
  },
  scheduleJob: async (jobId: string, scheduleExpression: string): Promise<any> => {
    const response = await apiClient.post(`/ingestion/jobs/${jobId}/schedule`, { schedule_expression: scheduleExpression });
    return response.data;
  },
  holdJob: async (jobId: string): Promise<any> => {
    const response = await apiClient.post(`/ingestion/jobs/${jobId}/hold`);
    return response.data;
  },
  restartJob: async (jobId: string): Promise<any> => {
    const response = await apiClient.post(`/ingestion/jobs/${jobId}/restart`);
    return response.data;
  },
  deleteJob: async (jobId: string): Promise<any> => {
    const response = await apiClient.delete(`/ingestion/jobs/${jobId}`);
    return response.data;
  },
  getJobHistory: async (jobId: string, params?: { limit?: number; offset?: number }): Promise<any> => {
    const response = await apiClient.get(`/ingestion/jobs/${jobId}/history`, { params });
    return response.data;
  },
};

// Organizations API
export const organizationsApi = {
  list: async (): Promise<any> => {
    const response = await apiClient.get('/organizations');
    return response.data;
  },
  create: async (data: { name: string; description?: string }): Promise<any> => {
    const response = await apiClient.post('/organizations', data);
    return response.data;
  },
  get: async (id: string): Promise<any> => {
    const response = await apiClient.get(`/organizations/${id}`);
    return response.data;
  },
  update: async (id: string, data: { name?: string; description?: string; status?: string }): Promise<any> => {
    const response = await apiClient.put(`/organizations/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<any> => {
    const response = await apiClient.delete(`/organizations/${id}`);
    return response.data;
  },
};

// User Access API
export const userAccessApi = {
  get: async (userId: string): Promise<any> => {
    const response = await apiClient.get(`/users/${userId}/access`);
    return response.data;
  },
  update: async (userId: string, data: {
    organization_id?: string;
    datasets?: string[];
    admin?: boolean;
    analysis_tier?: string;
    can_save_views?: boolean;
    can_share_views?: boolean;
  }): Promise<any> => {
    const response = await apiClient.put(`/users/${userId}/access`, data);
    return response.data;
  },
};

// Multi-Pool User Management API
// `env` selects which AWS account a Cognito pool call runs against —
// 'management' (default, the Hub's own admin pool) or 'dev'/'test'
// (cross-account into the portal-user spokes, ADO #4583). listPools()
// already aggregates all three and tags each pool with its env; callers
// pass that same env back on every subsequent call for that pool.
export const userPoolApi = {
  listPools: async (): Promise<any> => {
    const response = await apiClient.get('/users/pools');
    return strictApiResponse(response.data, 'pools');
  },
  listGroups: async (poolId: string, env?: string): Promise<any> => {
    const response = await apiClient.get('/users/groups', {
      params: { pool_id: poolId, ...(env ? { env } : {}) },
    });
    return safeApiResponse(response.data, { groups: [] });
  },
  listUsers: async (poolId?: string, env?: string): Promise<any> => {
    const params: Record<string, string> = {};
    if (poolId) params.pool_id = poolId;
    if (env) params.env = env;
    const response = await apiClient.get('/users', {
      params: Object.keys(params).length ? params : undefined,
    });
    return safeApiResponse(response.data, { users: [] });
  },
  createUser: async (data: { email: string; group: string; send_email: boolean; pool_id?: string; env?: string }): Promise<any> => {
    const response = await apiClient.post('/users', data);
    return response.data;
  },
  updateUser: async (username: string, data: { group?: string; enabled?: boolean; email?: string; pool_id?: string; tier?: string; env?: string }): Promise<any> => {
    const response = await apiClient.put(`/users/${encodeURIComponent(username)}`, data);
    return response.data;
  },
  deleteUser: async (username: string, poolId?: string, env?: string): Promise<any> => {
    const params: Record<string, string> = {};
    if (poolId) params.pool_id = poolId;
    if (env) params.env = env;
    const response = await apiClient.delete(`/users/${encodeURIComponent(username)}`, {
      params: Object.keys(params).length ? params : undefined,
    });
    return response.data;
  },
  resetPassword: async (username: string, data: { send_email: boolean; pool_id?: string; env?: string }): Promise<any> => {
    const response = await apiClient.post(`/users/${encodeURIComponent(username)}/reset-password`, data);
    return response.data;
  },
  resendInvite: async (username: string, poolId?: string, env?: string): Promise<any> => {
    const response = await apiClient.post(`/users/${encodeURIComponent(username)}/resend-invite`, {
      pool_id: poolId,
      env,
    });
    return response.data;
  },
};

// User Settings Types
export interface UserSettings {
  displayName?: string;
  email?: string;
  theme?: 'purple' | 'dark' | 'light' | 'contrast' | 'green';
  timezone?: string;
  dateFormat?: string;
  defaultTimeRange?: string;
  exportFormat?: 'json' | 'csv';
  notifications?: {
    anomalyAlerts?: boolean;
    dailyReports?: boolean;
    weeklySummaries?: boolean;
    systemAlerts?: boolean;
  };
}

export interface SettingsResponse {
  success: boolean;
  data: UserSettings;
  message?: string;
}

// Environment Colors (Task 1.1)
export const ENV_COLORS = {
  dev: { primary: '#2563eb', bg: '#dbeafe', text: '#1d4ed8' },
  test: { primary: '#f97316', bg: '#ffedd5', text: '#c2410c' },
  uat: { primary: '#7c3aed', bg: '#ede9fe', text: '#6d28d9' },
  prod: { primary: '#16a34a', bg: '#dcfce7', text: '#15803d' },
  shared: { primary: '#0891b2', bg: '#cffafe', text: '#0e7490' },
} as const;

// Health/Metrics Types (Task 1.1)
export interface ComponentHealth {
  status: 'healthy' | 'warning' | 'critical' | 'no_data' | 'not_found' | 'stopped' | 'error' | 'unknown';
  latency_p99?: number;
  error_rate?: number;
  invocations?: number;
  duration_avg?: number;
  cpu?: number;
  memory?: number;
  connections?: number;
  deployed?: boolean;
  [key: string]: any;
}

export interface HealthResponse {
  version: string;
  environment: string;
  overall_status: 'healthy' | 'warning' | 'critical' | 'unknown';
  components: {
    api_gateway?: ComponentHealth;
    lambda?: ComponentHealth;
    opensearch?: ComponentHealth;
    step_functions?: ComponentHealth;
    aurora?: ComponentHealth;
    eks?: ComponentHealth;
    dynamodb?: ComponentHealth;
    cognito?: ComponentHealth;
    cloudfront?: ComponentHealth;
    s3?: ComponentHealth;
  };
  timestamp: string;
}

export interface AlertItem {
  id: string;
  name: string;
  severity: 'critical' | 'warning' | 'info' | 'resolved';
  state: string;
  message: string;
  metric?: string;
  namespace?: string;
  threshold?: number;
  timestamp: string;
  state_reason?: string;
}

export interface AlertsResponse {
  version: string;
  environment: string;
  alerts: AlertItem[];
  summary: {
    critical: number;
    warning: number;
    info: number;
    resolved: number;
    total_active: number;
  };
  timestamp: string;
  /**
   * True only on getAlerts' own fallback path (the real request failed and
   * this is a synthesized empty response, not a genuine zero from the
   * source). Optional and absent on every real response, so existing
   * callers that don't check it are unaffected — but a caller that DOES
   * check it can tell "the estate has no alerts" apart from "we could not
   * ask" instead of rendering both as the same clean "no alerts" (DoD-07).
   */
  sourceUnreachable?: boolean;
}

export interface LogEntry {
  timestamp: string;
  message: string;
  level?: 'info' | 'warn' | 'error';
  source?: string;
}

export interface LogsResponse {
  version: string;
  environment: string;
  logs: LogEntry[];
  next_token?: string;
  timestamp: string;
}

// GET /logs/{buildId} — CodeBuild build status + log tail (per-env routed).
export interface BuildLogsResponse {
  id: string;
  status: string;
  start_time?: string | null;
  end_time?: string | null;
  current_phase?: string;
  phases?: Array<{
    name?: string;
    status?: string;
    start_time?: string | null;
    end_time?: string | null;
    duration?: number;
  }>;
  logs?: Array<{ timestamp?: string; message: string; level?: string; action?: string }>;
  next_token?: string;
  log_error?: string;
  log_message?: string;
  version?: string;
}

// Helper to safely handle API responses that might be HTML error pages
const safeApiResponse = <T>(data: any, fallback: T): T => {
  if (!data) return fallback;
  if (typeof data === 'string') {
    if (data.trim().startsWith('<!DOCTYPE') || data.trim().startsWith('<html')) {
      console.warn('API returned HTML instead of JSON - environment may not be deployed');
      return fallback;
    }
    try {
      return JSON.parse(data);
    } catch {
      return fallback;
    }
  }
  return data;
};

// Strict version that throws on error responses instead of returning fallback
const strictApiResponse = <T>(data: any, label: string): T => {
  if (!data) {
    throw new Error(`No response from ${label} API`);
  }
  if (typeof data === 'string') {
    if (data.trim().startsWith('<!DOCTYPE') || data.trim().startsWith('<html')) {
      throw new Error(`${label} API returned HTML — environment may not be deployed`);
    }
    try {
      const parsed = JSON.parse(data);
      if (parsed.error) {
        throw new Error(parsed.error);
      }
      return parsed;
    } catch (e) {
      if (e instanceof SyntaxError) {
        throw new Error(`${label} API returned invalid JSON`);
      }
      throw e;
    }
  }
  if (data.error) {
    throw new Error(data.error);
  }
  return data;
};

// Metrics API (Task 1.1) - Combines health, alerts, logs
export const metricsApi = {
  /**
   * Get health metrics for an environment
   */
  getHealth: async (
    environment: string = 'dev',
    component?: string,
    options?: { duration?: string; includeSeries?: boolean }
  ): Promise<HealthResponse> => {
    const fallback: HealthResponse = {
      version: '14.4.0',
      environment,
      overall_status: 'unknown',
      components: {},
      timestamp: new Date().toISOString()
    };
    try {
      const params: Record<string, string> = { environment };
      if (component) params.component = component;
      if (options?.duration) params.duration = options.duration;
      if (options?.includeSeries) params.include_series = 'true';
      const response = await apiClient.get<HealthResponse>('/health', { params });
      return safeApiResponse(response.data, fallback);
    } catch (err: any) {
      // Return fallback on error instead of throwing
      console.warn('Health API error:', err?.message);
      return fallback;
    }
  },

  /**
   * Get alerts for an environment
   */
  getAlerts: async (environment: string = 'dev', action: string = 'list'): Promise<AlertsResponse> => {
    const fallback: AlertsResponse = {
      version: '14.4.0',
      environment,
      alerts: [],
      summary: { critical: 0, warning: 0, info: 0, resolved: 0, total_active: 0 },
      timestamp: new Date().toISOString(),
      sourceUnreachable: true,
    };
    try {
      const response = await apiClient.get<AlertsResponse>('/alerts', {
        params: { environment, action },
      });
      // safeApiResponse falls back on an HTML/unparseable body too (a 200
      // that isn't real data) — that is exactly as "we could not really
      // read this" as a thrown request error, so it gets the same honest
      // flag rather than being indistinguishable from a genuine zero.
      return safeApiResponse(response.data, fallback);
    } catch (err: any) {
      console.warn('Alerts API error:', err?.message);
      return fallback;
    }
  },

  /**
   * Get alert summary counts
   */
  getAlertSummary: async (environment: string = 'dev'): Promise<AlertsResponse> => {
    const response = await apiClient.get<AlertsResponse>('/alerts', {
      params: { environment, action: 'summary' },
    });
    return response.data;
  },

  /**
   * Acknowledge an alert
   */
  acknowledgeAlert: async (alertId: string, user?: string): Promise<{ success: boolean; message?: string }> => {
    const response = await apiClient.post('/alerts', {
      action: 'acknowledge',
      alert_id: alertId,
      user,
    });
    return response.data;
  },

  /**
   * Get CloudWatch logs for an environment
   */
  getLogs: async (environment: string = 'dev', logGroup?: string, limit: number = 50): Promise<LogsResponse> => {
    const response = await apiClient.get<LogsResponse>('/logs', {
      params: { environment, log_group: logGroup, limit },
    });
    return response.data;
  },

  /**
   * Get a CodeBuild build's status + log tail (GET /logs/{buildId}).
   * `environment` routes the read to that spoke's account — promotion builds
   * run in the TARGET spoke's own terraform-executor, which the default
   * (dev-pinned) monitor role cannot see.
   */
  getBuildLogs: async (
    buildId: string,
    environment?: string,
    limit: number = 100,
  ): Promise<BuildLogsResponse> => {
    const response = await apiClient.get<BuildLogsResponse>(
      `/logs/${encodeURIComponent(buildId)}`,
      { params: { ...(environment ? { environment } : {}), limit } },
    );
    return response.data;
  },

  /**
   * Get audit trail chain health across all portals
   */
  getAuditHealth: async (): Promise<any> => {
    try {
      const response = await apiClient.get('/audit/health');
      return response.data;
    } catch {
      return {
        portals: [],
        total_events: 0,
      };
    }
  },
};

// Scholarly Research API
export const scholarApi = {
  /**
   * Search scholarly papers
   */
  search: async (params: {
    query: string;
    sources?: string[];
    limit?: number;
    offset?: number;
    filters?: Record<string, any>;
  }): Promise<any> => {
    const response = await apiClient.post('/scholar/search', params);
    return response.data;
  },

  /**
   * Get a specific paper by ID
   */
  getPaper: async (paperId: string): Promise<any> => {
    const response = await apiClient.get(`/scholar/papers/${paperId}`);
    return response.data;
  },

  /**
   * Get faceted search results for scholarly data
   */
  faceted: async (params: {
    field: string;
    query?: string;
    source?: string;
  }): Promise<any> => {
    const response = await apiClient.get('/scholar/facets', { params });
    return response.data;
  },

  /**
   * Get paper recommendations based on a paper ID
   */
  recommend: async (paperId: string, limit?: number): Promise<any> => {
    const response = await apiClient.get(`/scholar/recommend/${paperId}`, {
      params: { limit: limit || 10 },
    });
    return response.data;
  },

  /**
   * Get paper recommendations based on a text query
   */
  recommendFromQuery: async (query: string, limit?: number): Promise<any> => {
    const response = await apiClient.post('/scholar/recommend', {
      query,
      limit: limit || 10,
    });
    return response.data;
  },

  /**
   * Get scholarly analytics (citation trends, publication counts, etc.)
   */
  analytics: async (params?: {
    time_range?: string;
    source?: string;
    metric?: string;
  }): Promise<any> => {
    const response = await apiClient.get('/scholar/analytics', { params });
    return response.data;
  },

  /**
   * Export scholarly data
   */
  exportData: async (params: {
    format: 'json' | 'csv' | 'bibtex';
    query?: string;
    paper_ids?: string[];
    max_records?: number;
  }): Promise<any> => {
    const response = await apiClient.post('/scholar/export', params);
    return response.data;
  },

  /**
   * Get or manage scholarly alerts
   */
  alerts: async (params?: {
    action?: 'list' | 'create' | 'delete';
    alert_id?: string;
    query?: string;
    frequency?: 'daily' | 'weekly';
  }): Promise<any> => {
    if (params?.action === 'create') {
      const response = await apiClient.post('/scholar/alerts', params);
      return response.data;
    } else if (params?.action === 'delete' && params?.alert_id) {
      const response = await apiClient.delete(`/scholar/alerts/${params.alert_id}`);
      return response.data;
    }
    const response = await apiClient.get('/scholar/alerts', { params });
    return response.data;
  },
};

// Datastore Infrastructure - live query from ingestion Lambda
export interface DatastoreInfo {
  records: number;
  dataSize: string;
  rawFiles: number;
  rawSize: string;
  timeSpan: string;
  description: string;
  lastUpdated: string | null;
  schedule: string;
  status: 'active' | 'pending' | 'inactive';
  index: string;
}

export interface OpenSearchStorage {
  total_gb: number;
  used_gb: number | null;
  free_gb: number | null;
  percent_used: number | null;
}

export const datastoreApi = {
  getStats: async (): Promise<{
    success: boolean;
    data: {
      record_counts: Record<string, number>;
      datasource_details: Record<string, DatastoreInfo>;
      total_records: number;
      total_datasources: number;
      opensearch_storage?: OpenSearchStorage;
    };
  }> => {
    const response = await apiClient.post('/ingestion', { action: 'datastores' });
    return response.data;
  },
};

// ---------------------------------------------------------------------------
// Support Tickets API (Management Hub — Story #4613, Stream 9 / #4637)
//
// Read-mostly cross-portal support view. Reads return the HUB-SAFE projection
// (public-only: internal notes/attachments never appear). The write surface is
// EXACTLY status/priority PATCH (conditional on version), a public text reply,
// and Retry sync now — matching the hub Lambda's least-privilege boundary.
// ---------------------------------------------------------------------------
export interface SupportEvent {
  seq: number;
  event_type: string;
  actor: string;
  visibility: string;
  created_at: string;
  body?: Record<string, unknown>;
}

export interface SupportAttachment {
  attachment_id: string;
  filename?: string;
  content_type?: string;
  size_bytes?: number;
  visibility?: string;
  scan_status?: string;
  created_at?: string;
  is_video?: boolean;
}

export interface SupportTicket {
  ticket_id: string;
  ref?: string;
  subject?: string;
  status?: string;
  priority?: string;
  portal?: string;
  request_type?: string;
  user_id?: string;
  assignee?: string | null;
  created_at?: string;
  updated_at?: string;
  age_seconds?: number | null;
  sync_status?: string;
  ado_id?: string;
  ado_url?: string;
  duplicate?: boolean;
  duplicate_of?: string;
  escalated?: boolean;
  last_escalated_at?: string;
  security_review?: boolean;
  version?: number;
  events?: SupportEvent[];
  attachments?: SupportAttachment[];
  failed_syncs?: { outbox_id: string; op?: string; last_error?: string }[];
}

export interface SupportStats {
  open: number;
  unassigned: number;
  median_first_response_hours: number | null;
  aging_over_7d: number;
  resolved_30d: number;
  sync_failures: number;
}

export interface SupportListResponse {
  tickets: SupportTicket[];
  stats: SupportStats;
  source?: { account: string; environment: string; portals: string[] };
}

export const supportApi = {
  /** Hub queue + live tiles (all numbers from real queue-index queries). */
  list: async (): Promise<SupportListResponse> => {
    const response = await apiClient.get<SupportListResponse>('/support/tickets');
    return response.data;
  },
  /** Hub-originated audit ticket (Admins only) — records an admin action such
   *  as a credential email so it is visible in the Support queue. */
  createTicket: async (data: {
    subject: string;
    description: string;
    user_id?: string;
    portal?: string;
    request_type?: string;
  }): Promise<{ ticket_id: string; ref: string; status: string }> => {
    const response = await apiClient.post('/support/tickets', data);
    return response.data;
  },
  /** Hub-safe ticket detail (public-only thread + attachments). */
  get: async (ticketId: string): Promise<SupportTicket> => {
    const response = await apiClient.get<SupportTicket>(`/support/tickets/${ticketId}`);
    return response.data;
  },
  /** Public text reply (text-only — no internal notes / uploads from the hub). */
  reply: async (ticketId: string, message: string): Promise<{ ok: boolean }> => {
    const response = await apiClient.post(`/support/tickets/${ticketId}/reply`, { message });
    return response.data;
  },
  /** status/priority PATCH, conditional on the loaded version (optimistic lock). */
  patch: async (
    ticketId: string,
    changes: { status?: string; priority?: string; version: number },
  ): Promise<{ ok: boolean; status?: string; priority?: string; version: number }> => {
    const response = await apiClient.patch(`/support/tickets/${ticketId}`, changes);
    return response.data;
  },
  /** Retry sync now — reset a FAILED outbox row to pending. */
  retrySync: async (ticketId: string, outboxId: string): Promise<{ ok: boolean }> => {
    const response = await apiClient.post(`/support/tickets/${ticketId}/retry-sync`, {
      outbox_id: outboxId,
    });
    return response.data;
  },
};

// User Settings API (Task 1.3)
export const settingsApi = {
  /**
   * Get current user's settings
   */
  getSettings: async (): Promise<SettingsResponse> => {
    const response = await apiClient.get<SettingsResponse>('/settings');
    return response.data;
  },

  /**
   * Update user settings
   */
  updateSettings: async (settings: Partial<UserSettings>): Promise<SettingsResponse> => {
    const response = await apiClient.put<SettingsResponse>('/settings', settings);
    return response.data;
  },

  /**
   * Get available themes
   */
  getThemes: async (): Promise<{ success: boolean; data: { themes: string[] } }> => {
    const response = await apiClient.get('/settings/themes');
    return response.data;
  },
};

// Browse API Types
export interface BrowseParams {
  query?: string;
  page: number;
  pageSize: number;
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
  filters?: Record<string, string[]>;
  facetFields?: string[];
  searchAfter?: any[];
}

export interface BrowseField {
  name: string;
  type: string;
  keyword: boolean;
  sortable: boolean;
  large: boolean;
}

export interface FacetBucket {
  value: string;
  count: number;
}

export interface BrowseResult {
  results: Record<string, any>[];
  total: number;
  searchAfter?: any[];
  facets?: Record<string, FacetBucket[]>;
}

// Partner API client (separate base URL for partner service)
const getPartnerApiUrl = (): string => {
  const config = (window as any).SECUREEMBED_CONFIG;
  if (config?.PARTNER_API_ENDPOINT) {
    return config.PARTNER_API_ENDPOINT;
  }
  // Fall back to main API
  return getApiBaseUrl();
};

const partnerApiClient: AxiosInstance = axios.create({
  timeout: API_TIMEOUT,
  headers: { 'Content-Type': 'application/json' },
});

partnerApiClient.interceptors.request.use((config) => {
  config.baseURL = getPartnerApiUrl();
  const token = localStorage.getItem(STORAGE_KEYS.ID_TOKEN) || localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Costs API - Live AWS Cost Explorer data
export const costsApi = {
  getCosts: async (period: string = '30d', startDate?: string, endDate?: string, environment?: string): Promise<any> => {
    const params: Record<string, string> = {};
    if (startDate && endDate) {
      params.start_date = startDate;
      params.end_date = endDate;
    } else {
      params.period = period;
    }
    // Story #4869: the handler now genuinely reads `environment` and filters
    // Cost Explorer by LINKED_ACCOUNT. Until then this comment claimed a
    // behaviour that did not exist -- the parameter was sent and ignored, so the
    // selector was decorative and every environment showed the same figures.
    if (environment) params.environment = environment;
    const response = await apiClient.get('/costs', { params });
    return response.data;
  },
  // Both drill-downs forward `environment` (Story #4869). Without it a
  // drill-down launched from a dev-scoped chart returned org-wide services and
  // silently contradicted the chart it came from.
  getDailyServices: async (date: string, environment?: string): Promise<any> => {
    const params: Record<string, string> = { action: 'daily_services', date };
    if (environment) params.environment = environment;
    const response = await apiClient.get('/costs', { params });
    return response.data;
  },
  getServiceTrend: async (service: string, period: string = '30d', environment?: string): Promise<any> => {
    const params: Record<string, string> = { action: 'service_trend', service, period };
    if (environment) params.environment = environment;
    const response = await apiClient.get('/costs', { params });
    return response.data;
  },
};

// Datastores not yet ingested (browse disabled)
const EMPTY_BROWSE_DATASTORES: string[] = [];

// Browse API - unified record browsing via ingestion Lambda → OpenSearch
export const browseApi = {
  /**
   * Browse records in a datastore with pagination, sorting, filtering
   */
  browse: async (datastore: string, params: BrowseParams): Promise<BrowseResult> => {
    // Empty datastores return immediately
    if (EMPTY_BROWSE_DATASTORES.includes(datastore)) {
      return { results: [], total: 0 };
    }

    // Route through ingestion Lambda which queries OpenSearch directly
    const body: Record<string, any> = {
      action: 'browse',
      datastore,
      query: params.query || '',
      page: params.page,
      pageSize: params.pageSize,
      sortField: params.sortField,
      sortOrder: params.sortOrder,
    };
    if (params.searchAfter) {
      body.searchAfter = params.searchAfter;
    }
    if (params.filters && Object.keys(params.filters).length > 0) {
      body.filters = params.filters;
    }
    if (params.facetFields && params.facetFields.length > 0) {
      body.facetFields = params.facetFields;
    }
    const response = await apiClient.post('/ingestion', body);

    const data = response.data;
    return {
      results: data?.results || [],
      total: data?.total || 0,
      searchAfter: data?.searchAfter || undefined,
      facets: data?.facets || undefined,
    };
  },

  getFields: async (datastore: string): Promise<BrowseField[]> => {
    if (EMPTY_BROWSE_DATASTORES.includes(datastore)) {
      return [];
    }
    const response = await apiClient.post('/ingestion', {
      action: 'browse-fields',
      datastore,
    });
    return response.data?.fields || [];
  },

  /**
   * Get a single record's full metadata
   */
  getRecord: async (_datastore: string, id: string): Promise<Record<string, any> | null> => {
    try {
      const response = await apiClient.get('/metadata', {
        params: { embedding_ids: id },
      });
      const data = response.data;
      const results = data?.data?.results || data?.results || [];
      return results[0] || null;
    } catch {
      return null;
    }
  },
};

// Energy Organisation Ingestion API (Layer 07j)
export const energyOrgApi = {
  getStatus: async (params: { env: string }): Promise<any> => {
    const response = await apiClient.post('/ingestion', { action: 'energy-org-status', environment: params.env });
    return response.data;
  },
  run: async (params: { env: string; source: string }): Promise<any> => {
    const response = await apiClient.post('/ingestion', { action: 'energy-org-run', environment: params.env, source: params.source });
    return response.data;
  },
  schedule: async (params: { env: string; schedule: string }): Promise<any> => {
    const response = await apiClient.post('/ingestion', { action: 'energy-org-schedule', environment: params.env, schedule: params.schedule });
    return response.data;
  },
  stop: async (params: { env: string }): Promise<any> => {
    const response = await apiClient.post('/ingestion', { action: 'energy-org-stop', environment: params.env });
    return response.data;
  },
  search: async (params: { query: string; limit?: number; filters?: Record<string, any> }): Promise<any> => {
    const response = await apiClient.post('/ingestion', { action: 'energy-org-search', ...params });
    return response.data;
  },
};

// Developer VMs API (lifecycle management)
export const developerVmApi = {
  list: async (params: { env: string }): Promise<any> => {
    const response = await apiClient.get('/developer-vms', { params: { environment: params.env } });
    return response.data?.data || response.data;
  },
  start: async (params: { env: string; vm_id: string }): Promise<any> => {
    const response = await apiClient.post('/developer-vms', { action: 'start', environment: params.env, vm_id: params.vm_id });
    return response.data;
  },
  stop: async (params: { env: string; vm_id: string; confirm: boolean }): Promise<any> => {
    const response = await apiClient.post('/developer-vms', { action: 'stop', environment: params.env, vm_id: params.vm_id, confirm: params.confirm });
    return response.data;
  },
  reboot: async (params: { env: string; vm_id: string }): Promise<any> => {
    const response = await apiClient.post('/developer-vms', { action: 'reboot', environment: params.env, vm_id: params.vm_id });
    return response.data;
  },
  resize: async (params: { env: string; vm_id: string; instance_type?: string; volume_size?: number }): Promise<any> => {
    const response = await apiClient.post('/developer-vms', { action: 'resize', environment: params.env, vm_id: params.vm_id, instance_type: params.instance_type, volume_size: params.volume_size });
    return response.data;
  },
  reset: async (params: { env: string; vm_id: string; double_confirm: boolean }): Promise<any> => {
    const response = await apiClient.post('/developer-vms', { action: 'reset', environment: params.env, vm_id: params.vm_id, double_confirm: params.double_confirm });
    return response.data;
  },
  create: async (params: { env: string; developer_name: string; email: string; instance_type: string; volume_size: number; auto_shutdown_time: string }): Promise<any> => {
    const response = await apiClient.post('/developer-vms', { action: 'create', environment: params.env, developer_name: params.developer_name, email: params.email, instance_type: params.instance_type, volume_size: params.volume_size, auto_shutdown_time: params.auto_shutdown_time });
    return response.data;
  },
  destroy: async (params: { env: string; vm_id: string; type_confirm: string; developer_name: string }): Promise<any> => {
    const response = await apiClient.post('/developer-vms', { action: 'destroy', environment: params.env, vm_id: params.vm_id, type_confirm: params.type_confirm, developer_name: params.developer_name });
    return response.data;
  },
  status: async (params: { env: string; vm_id: string }): Promise<any> => {
    const response = await apiClient.post('/developer-vms', { action: 'status', environment: params.env, vm_id: params.vm_id });
    return response.data;
  },
};

// =============================================================================
// =============================================================================

export interface PortalSettings {
  portal_id: string;
  portal_name: string;
  ai_model_document: string;
  ai_model_folder: string;
  ai_model_project: string;
  subscription_tiers: string;
  trial_duration_days: number;
  trial_credit_usd: number;
  stripe_secret_key_ssm: string;
  stripe_publishable_key_ssm: string;
  stripe_webhook_secret_ssm: string;
  updated_at?: string;
  updated_by?: string;
}

// =============================================================================
// Model Configuration Admin API (Story #5164 / Story #5010 Phase 4)
// =============================================================================

export interface ModelConfigRole {
  role: string;
  model_id: string | null;
  wire_format: string | null;
  version: number | null;
  source: 'portal_role_assignment' | 'platform_role_default' | 'compile_time_default' | null;
  is_embedding: boolean;
  is_override: boolean;
  residency_compliant: boolean | null;
}

export interface ModelConfigResolvedView {
  portal: string;
  roles: ModelConfigRole[];
  override_count: number;
}

export interface ModelConfigWriteResult {
  scope: string;
  target: string;
  model_id: string;
  version: number;
  full_target: string;
}

export interface ModelConfigStaleWriteError {
  error: 'stale_write';
  reason: string;
  current: Record<string, unknown> | null;
}

export interface ModelConfigGuardRejection {
  error: 'guard_rejection' | 'embedding_role_locked';
  reasons?: string[];
  reason?: string;
}

export interface ModelConfigHistoryEntry {
  scope_target: string;
  version: number;
  scope: string;
  target: string;
  model_id: string;
  wire_format: string;
  written_at: string;
  written_by: string;
  previous_version: number | null;
}

export interface ModelConfigCandidate {
  model_id: string;
  compliant: boolean;
  reasons: string[];
  families: string[];
  verified_by: string | null;
  rate_usd_per_unit: { input_per_million?: number; output_per_million?: number } | null;
}

export interface ModelConfigCandidatesResponse {
  role: string;
  required_wire_formats: string[];
  anomalous_wire_formats: string[];
  candidates: ModelConfigCandidate[];
}

export const modelConfigApi = {
  getResolved: async (portalId: string): Promise<ModelConfigResolvedView> => {
    const response = await apiClient.get('/model-config', { params: { portal: portalId } });
    return response.data;
  },
  getCandidates: async (role: string): Promise<ModelConfigCandidatesResponse> => {
    const response = await apiClient.get('/model-config/candidates', { params: { role } });
    return response.data;
  },
  putPlatformRole: async (
    role: string,
    modelId: string,
    expectedVersion: number | null,
    wireFormat?: string,
  ): Promise<ModelConfigWriteResult> => {
    const response = await apiClient.put(`/model-config/platform/role/${role}`, {
      model_id: modelId, expected_version: expectedVersion, wire_format: wireFormat,
    });
    return response.data;
  },
  putPortalRole: async (
    portalId: string,
    role: string,
    modelId: string,
    expectedVersion: number | null,
    wireFormat?: string,
  ): Promise<ModelConfigWriteResult> => {
    const response = await apiClient.put(`/model-config/portal/${portalId}/role/${role}`, {
      model_id: modelId, expected_version: expectedVersion, wire_format: wireFormat,
    });
    return response.data;
  },
  putSite: async (
    siteId: string,
    modelId: string,
    expectedVersion: number | null,
    wireFormat?: string,
  ): Promise<ModelConfigWriteResult> => {
    const response = await apiClient.put(`/model-config/site/${siteId}`, {
      model_id: modelId, expected_version: expectedVersion, wire_format: wireFormat,
    });
    return response.data;
  },
  getHistory: async (fullTarget: string): Promise<{ full_target: string; history: ModelConfigHistoryEntry[] }> => {
    const response = await apiClient.get(`/model-config/history/${encodeURIComponent(fullTarget)}`);
    return response.data;
  },
};

export const portalSettingsApi = {
  list: async (): Promise<{ portals: PortalSettings[]; count: number }> => {
    const response = await apiClient.get('/portal-settings');
    return response.data;
  },
  get: async (portalId: string): Promise<{ settings: PortalSettings }> => {
    const response = await apiClient.get(`/portal-settings/${portalId}`);
    return response.data;
  },
  update: async (portalId: string, settings: Partial<PortalSettings>): Promise<{ settings: PortalSettings }> => {
    const response = await apiClient.put(`/portal-settings/${portalId}`, settings);
    return response.data;
  },
};

// =============================================================================
// Pricing Configuration API (per-portal versioned pricing schema)
// =============================================================================

export interface PricingTier {
  id: string;
  name: string;
  price: number | string;
  per: string;
  gst: 'inclusive' | 'exclusive';
  inc: string[];
  pub: boolean;
  feat: boolean;
}

export interface PricingSeatClass {
  class: string;
  price: number | string;
}

export interface PricingMeteredItem {
  key: string;
  label: string;
  price: number | string;
  unit: string;
}

export interface PricingAddon {
  key: string;
  label: string;
  price: number | string;
  unit: string;
}

export interface PricingSectorRoutes {
  education_discount_pct: string;
  government_route: string;
  founding_coupon: string;
}

export interface PricingConfig {
  portal_label: string;
  currency: string;
  gst_mode: string;
  cadence: string;
  tiers: PricingTier[];
  seat_classes: PricingSeatClass[];
  metered: PricingMeteredItem[];
  addons: PricingAddon[];
  sector_routes: PricingSectorRoutes;
}

export interface PricingPortalSummary {
  portal_id: string;
  portal_label?: string;
  color?: string;
  status?: 'draft' | 'published';
  version?: number;
}

export interface PricingGetResponse {
  portal_id: string;
  draft?: PricingConfig | null;
  published?: PricingConfig | null;
  draft_version?: number;
  published_version?: number;
  status?: 'draft' | 'published';
}

export interface PricingVersion {
  version: number;
  status: 'draft' | 'published' | 'archived';
  author?: string;
  note?: string;
  created_at?: string;
}

export interface PricingSyncResult {
  mode: 'test' | 'live';
  created: number;
  updated: number;
  skipped: number;
  details?: Array<{ name: string; action: string; stripe_id?: string }>;
  message?: string;
}

export const pricingApi = {
  list: async (): Promise<{ portals: PricingPortalSummary[]; count?: number }> => {
    const response = await apiClient.get('/pricing');
    return response.data;
  },
  get: async (portalId: string): Promise<PricingGetResponse> => {
    const response = await apiClient.get(`/pricing/${portalId}`);
    return response.data;
  },
  saveDraft: async (portalId: string, config: PricingConfig): Promise<PricingGetResponse> => {
    const response = await apiClient.put(`/pricing/${portalId}/draft`, config);
    return response.data;
  },
  publish: async (portalId: string): Promise<PricingGetResponse> => {
    const response = await apiClient.post(`/pricing/${portalId}/publish`);
    return response.data;
  },
  versions: async (portalId: string): Promise<{ versions: PricingVersion[] }> => {
    const response = await apiClient.get(`/pricing/${portalId}/versions`);
    return response.data;
  },
  restore: async (portalId: string, version: number): Promise<PricingGetResponse> => {
    const response = await apiClient.post(`/pricing/${portalId}/restore`, { version });
    return response.data;
  },
  syncStripe: async (portalId: string): Promise<PricingSyncResult> => {
    const response = await apiClient.post(`/pricing/${portalId}/sync-stripe`);
    return response.data;
  },
};

// =============================================================================
// =============================================================================

import { createOrgApi } from '@secureembed/shared-crm';

export const orgApi = createOrgApi({ apiClient });

// ---------------------------------------------------------------------------
// Knowledge — the OKF bundle, its decision records, and the first-party source
// the concepts describe. ADR-013: served ONLY from the authenticated hub API,
// out of a private CMK-encrypted bucket, never from this SPA's own origin (which
// answers 200 unauthenticated on every path).
//
// The `path` sent to getFile is a repository path, and the Lambda looks it up in
// the pack manifest. It is never used to build an S3 key, so an unknown path is
// a 404 by the same route as a typo.
// ---------------------------------------------------------------------------
export interface KnowledgeConcept {
  path: string;
  zone: string;
  name: string;
  bytes: number;
  sha256: string;
}

/** The bundle's own freshness measure, from scripts/okf-divergence.py. */
export interface KnowledgeDivergence {
  /** null means it could not be computed — never render that as 0. */
  score: number | null;
  components?: Record<string, number>;
  note?: string;
}

/** A diagram, its render, and whether that render still matches its source. */
export interface KnowledgeDiagram {
  path: string;
  root: string;
  format: string;
  render: string;
  render_state: 'rendered' | 'stale' | 'source-only' | 'too-large' | 'missing' | string;
  sources: string[];
}

export interface KnowledgeOverview {
  commit: string;
  built_at?: string;
  roots: string[];
  counts: {
    files: number;
    concepts: number;
    diagrams?: number;
    diagrams_rendered?: number;
    diagrams_stale?: number;
  };
  divergence?: KnowledgeDivergence;
  diagrams?: KnowledgeDiagram[];
  concepts: KnowledgeConcept[];
  version?: string;
}

export interface KnowledgeFile {
  path: string;
  /** Text, or base64 when `binary` is true (a diagram render). */
  content: string;
  language: string;
  bytes: number;
  sha256: string;
  commit: string;
  binary?: boolean;
  media_type?: string;
}

export const knowledgeApi = {
  getOverview: async (): Promise<KnowledgeOverview> => {
    const response = await apiClient.get('/knowledge');
    return response.data;
  },
  listConcepts: async (): Promise<{ commit: string; concepts: KnowledgeConcept[] }> => {
    const response = await apiClient.get('/knowledge/concepts');
    return response.data;
  },
  getFile: async (path: string): Promise<KnowledgeFile> => {
    const response = await apiClient.get('/knowledge/file', { params: { path } });
    return response.data;
  },
};
